import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Button } from '../design-system/Button';
import { Shuffle } from '../design-system';
import type { IframePoolOrder } from '../api';
import {
  EXPAND_DELAY_MS,
  INITIAL_CENTER_INDEX,
  MAX_SPIN_MS,
  MAX_SPIN_STEPS,
  MIN_SPIN_MS,
  MIN_SPIN_STEPS,
  REVEAL_DELAY_MS,
  SETTLE_MS,
  TILE_COUNT,
  UNVEIL_MS,
  dimFor,
  randInt,
  type AnimMode,
} from './readyStep.constants';
import { useTileStep } from './useTileStep';
import { SlotTile } from './SlotTile';
import { slotSpinAnimation } from './slotSpinAnimation';

export interface ReadyStepProps {
  offer: IframePoolOrder['poolOffer'];
  /** Fired the moment the wheel hands off to the `reveal` phase
   *  (white glow appears, pack tile is gone). The parent uses this
   *  signal to dispatch `mark_as_opened` on the backend — the
   *  resulting badge name comes back via `revealedBadge` and is
   *  rendered by the overlay siblings (`<RevealBackgroundTint />`,
   *  `<RevealRays />`) that live above `ReadyStep`. */
  onRevealStarted?: () => void;
  /** Fires when the reveal phase has held long enough and it's
   *  time to wipe the viewport white. Parent mounts the
   *  `<RevealWhiteWipe />` overlay (full-screen, NOT clipped by the
   *  lane viewport) which handles the expand + swap + fade. */
  onExpansionStart?: () => void;
}

/**
 * Ready step — slot-machine lane. Figma 702:8017 (desktop) / 702:5485
 * (mobile). The lane is a horizontal carousel of the same pack image
 * repeated; clicking Shuffle dims away the neighbour tint, spins the
 * wheel for ~3-6s with a real-slot velocity curve, motion blur, and
 * overshoot rebound, settles, fades the neighbours, then bursts into
 * a white-glow + rotating-rays reveal.
 *
 * State machine (each phase delegated to its own helper / overlay):
 *   idle    → Shuffle press → unveil   (UNVEIL_MS)
 *   unveil  → spin           (3-6 s slot animation)
 *   spin    → settle pause   → cleared (CLEAR_TRANSITION_MS fade)
 *   cleared → REVEAL_DELAY_MS → reveal (glow + rays, infinite)
 *
 * At the moment we enter `reveal` we fire `onRevealStarted` so the
 * parent can dispatch `mark_as_opened`; the response's badge name
 * is consumed by `RevealBackgroundTint` / `RevealRays` (siblings
 * mounted by `PackReveal`), which flash from plain white to the
 * rarity-specific tint via `glowColorFor()`.
 */
export function ReadyStep({
  offer,
  onRevealStarted,
  onExpansionStart,
}: ReadyStepProps) {
  const { ref: tileRef, tileStep } = useTileStep();
  const [animMode, setAnimMode] = useState<AnimMode>('idle');
  const [spinSteps, setSpinSteps] = useState(0);
  const [spinDuration, setSpinDuration] = useState(0);
  const unveilTimerRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);
  const expandTimerRef = useRef<number | null>(null);
  // Latest-callback refs — PackReveal re-renders on every machine
  // state change, recreating these callbacks each time. Capturing
  // them in refs lets the reveal/expand effects depend on `animMode`
  // only, so the timers don't keep resetting and missing their fire
  // time.
  const onRevealStartedRef = useRef(onRevealStarted);
  const onExpansionStartRef = useRef(onExpansionStart);
  useEffect(() => {
    onRevealStartedRef.current = onRevealStarted;
  }, [onRevealStarted]);
  useEffect(() => {
    onExpansionStartRef.current = onExpansionStart;
  }, [onExpansionStart]);
  // Respect OS-level `prefers-reduced-motion`. When the user opts out
  // of animation, Shuffle short-circuits straight to completion — no
  // unveil-fade, no spin, no settle. Motion-sickness users still get
  // a working CTA.
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    return () => {
      if (unveilTimerRef.current !== null) {
        window.clearTimeout(unveilTimerRef.current);
      }
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
      }
      if (expandTimerRef.current !== null) {
        window.clearTimeout(expandTimerRef.current);
      }
    };
  }, []);

  // After the rays have spun for a beat in `reveal`, advance to
  // `expand` and notify the parent — it decides whether to mount
  // the white wipe (only when `mark_as_opened` actually returned a
  // badge). Long-ish delay (~2 s) so the rotating rays + rarity
  // tint have time to register before the wipe kicks in.
  useEffect(() => {
    if (animMode !== 'reveal') return;
    expandTimerRef.current = window.setTimeout(() => {
      setAnimMode('expand');
      expandTimerRef.current = null;
      onExpansionStartRef.current?.();
    }, EXPAND_DELAY_MS);
    return () => {
      if (expandTimerRef.current !== null) {
        window.clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    };
  }, [animMode]);

  // After the neighbours have faded (`cleared`), pause briefly and
  // then advance to `reveal` — landed pack crossfades to a white glow
  // square and the sun-burst rays start rotating. Notify the parent
  // so it can dispatch `mark_as_opened` on the backend; the response's
  // badge drives the overlay tint via sibling components.
  useEffect(() => {
    if (animMode !== 'cleared') return;
    revealTimerRef.current = window.setTimeout(() => {
      setAnimMode('reveal');
      revealTimerRef.current = null;
      onRevealStartedRef.current?.();
    }, REVEAL_DELAY_MS);
    return () => {
      if (revealTimerRef.current !== null) {
        window.clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [animMode]);

  const handleShuffle = () => {
    if (animMode !== 'idle') return;

    // Reduced motion: skip every animated phase and jump straight
    // into `reveal` so the parent still dispatches `mark_as_opened`.
    if (prefersReducedMotion) {
      setAnimMode('reveal');
      onRevealStarted?.();
      return;
    }

    // Fade out the neighbour tint first, then start the wheel — gives
    // the lane a half-beat to "wake up" before motion.
    setAnimMode('unveil');
    unveilTimerRef.current = window.setTimeout(() => {
      const steps = randInt(MIN_SPIN_STEPS, MAX_SPIN_STEPS);
      const duration =
        MIN_SPIN_MS + Math.random() * (MAX_SPIN_MS - MIN_SPIN_MS);
      setSpinSteps(steps);
      setSpinDuration(duration);
      setAnimMode('spin');
      unveilTimerRef.current = null;
    }, UNVEIL_MS);
  };

  const handleLaneAnimComplete = () => {
    if (animMode !== 'spin') return;
    // Beat on the landed tile, then dissolve the neighbours so only
    // the focal pack remains. `spinSteps` is preserved so per-tile
    // opacity logic can identify the visual centre after the spin.
    window.setTimeout(() => {
      setAnimMode('cleared');
    }, SETTLE_MS);
  };

  const { laneAnimate, laneTransition, blurAnimate, blurTransition } =
    slotSpinAnimation({ animMode, spinSteps, spinDuration, tileStep });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // No `exit` here on purpose. AnimatePresence + a 350 ms
      // exit-fade left the slot lane visibly bleeding through for
      // ~half a beat when status flipped to `winner` — both for
      // the badge-present path (where the white wipe is supposed
      // to hide the swap) and the badge-absent path (where the
      // user could see the lane "loading out" before WinnerStep
      // appeared). Without an exit prop the unmount is atomic.
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="relative flex h-full w-full flex-col items-center"
    >
      {/* Viewport — clips the lane horizontally. No mask here: the
          fade-to-transparent runs on the lane itself so it doesn't
          eat the page background when the operator picks a bright
          colour. Per-tile `filter: brightness(…)` does the focus job
          that the spotlight overlay used to. */}
      <div className="relative flex flex-1 w-full items-center justify-center overflow-hidden">
        <motion.div
          // No mask — the viewport's `overflow-hidden` already crops
          // off-screen tiles. A horizontal mask-fade was leaving tiles
          // partially transparent in the fade-zone, and during spin
          // every tile passed through that band → visually they
          // "докручиваются полупрозрачными" past the centre.
          className="will-change-transform [transform:translateZ(0)]"
          animate={laneAnimate}
          transition={laneTransition}
          onAnimationComplete={handleLaneAnimComplete}
        >
          <motion.div
            className="flex items-center gap-3 @[500px]:gap-[30px]"
            animate={blurAnimate}
            transition={blurTransition}
          >
            {Array.from({ length: TILE_COUNT }).map((_, i) => {
              const distance = Math.abs(i - INITIAL_CENTER_INDEX);
              // Apply the resting tint only when truly idle; during
              // unveil / spin / snap every tile is at full brightness
              // so the spinning lane reads as one bright reel.
              const brightness = animMode === 'idle' ? dimFor(distance) : 1;
              const isCenter = i === INITIAL_CENTER_INDEX;
              const landedIndex = INITIAL_CENTER_INDEX + spinSteps;
              const isLanded = i === landedIndex;
              return (
                <SlotTile
                  key={i}
                  ref={isCenter ? tileRef : undefined}
                  packImageUrl={offer.packImageUrl}
                  title={offer.title}
                  brightness={brightness}
                  isCenter={isCenter}
                  isLanded={isLanded}
                  animMode={animMode}
                />
              );
            })}
          </motion.div>
        </motion.div>

        {/* Glow square no longer lives here — it's a single
            `<RevealGlowOverlay>` mounted by PackReveal on its root,
            so the SAME node handles the steady reveal-phase plate
            AND the scale-up expand-phase wipe (avoids the visible
            "second element appearing" hop that used to happen when
            an in-viewport glow was swapped for a separate wipe). */}
      </div>

      {/* CTA — Figma 711:4476. Desktop fixes width to 357px; on smaller
          screens the pill stretches edge-to-edge with a 16px gutter.
          The label + icon live in an explicit inline-flex span so the
          two never wrap onto separate lines, no matter what Button's
          internal wrapping span ends up being. */}
      <div className="w-full px-4 pb-6 max-w-[357px] mx-auto">
        <Button
          variant="primary"
          onClick={handleShuffle}
          disabled={animMode !== 'idle'}
          fullWidth
          className="h-16 type-headline"
        >
          <span className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap">
            Shuffle
            <Shuffle size={24} />
          </span>
        </Button>
      </div>

      {/* Rays live one level up in <PackReveal /> so their rotation
          continues smoothly into WinnerStep — they're NOT rendered
          here any more. */}
    </motion.div>
  );
}
